import { NodeRosterIdMappingInfo, PlanGroupSummary } from "../../admin-api/generated-src";
import {
  COMPLETED_STATUS,
  OVERRIDDEN_STATUS,
  ROW_COLOR_GREEN,
  ROW_COLOR_GREY,
  ROW_COLOR_RED,
  ROW_COLOR_YELLOW,
  SCHEDULED_STATUS,
  STATUS_TEXT_GREEN,
  STATUS_TEXT_GREY,
  STATUS_TEXT_RED,
  STATUS_TEXT_YELLOW,
} from "../../constants";

export interface ShiftStatus {
  status?: string;
  startTime?: string;
  endTime?: string;
}

/**
 * Resolves the PlanGroupSummary whose status drives the stats row. Status travels on the picker entry
 * itself (single source of truth), so the badge reads off the selected plan group rather than a
 * separate, differently-filtered list. Returns undefined when nothing is selected, which the caller
 * renders as the red "Invalid" status.
 */
export function findSelectedPlanGroup(
  planGroups: PlanGroupSummary[],
  selectedPlanGroupId: string | undefined
): PlanGroupSummary | undefined {
  return planGroups.find((pg) => pg.planGroupId === selectedPlanGroupId);
}

/**
 * Resolves the status to show. When a plan group is selected (CH or legacy shift in the picker), it
 * carries its own status — the single source of truth. On the no-plan-groups legacy node path
 * (RosterSelect), nothing is selected, so fall back to the nodeRosterIdMappingInfo list keyed by
 * currentRosterId, which the legacy fetch still populates. Returns undefined when neither resolves ->
 * caller renders Invalid.
 */
export function resolveShiftStatus(
  planGroups: PlanGroupSummary[],
  selectedPlanGroupId: string | undefined,
  rosterIdList: NodeRosterIdMappingInfo[],
  currentRosterId: string | undefined
): ShiftStatus | undefined {
  const selectedPlanGroup = findSelectedPlanGroup(planGroups, selectedPlanGroupId);
  if (selectedPlanGroup) {
    return selectedPlanGroup.status
      ? { status: selectedPlanGroup.status, startTime: selectedPlanGroup.startTime, endTime: selectedPlanGroup.endTime }
      : undefined;
  }
  if (selectedPlanGroupId === undefined && currentRosterId !== undefined) {
    const info = rosterIdList.find((roster) => roster.rosterId === currentRosterId);
    return info?.rosterStatus
      ? { status: info.rosterStatus, startTime: info.rosterStartTime, endTime: info.rosterEndTime }
      : undefined;
  }
  return undefined;
}

/**
 * Maps a resolved shift status to its text + row color classes. Completed and Overridden share the
 * green treatment (both are terminal, non-actionable states); Scheduled is grey, everything else
 * (e.g. InProgress) is yellow. Callers handle the unresolved/Invalid case separately and render red.
 */
export function statusColorClasses(status: string): { textClass: string; rowClass: string } {
  if (status === SCHEDULED_STATUS) {
    return { textClass: STATUS_TEXT_GREY, rowClass: ROW_COLOR_GREY };
  }
  if (status === COMPLETED_STATUS || status === OVERRIDDEN_STATUS) {
    return { textClass: STATUS_TEXT_GREEN, rowClass: ROW_COLOR_GREEN };
  }
  return { textClass: STATUS_TEXT_YELLOW, rowClass: ROW_COLOR_YELLOW };
}

/** Text + row classes for the unresolved / no-status case. */
export const INVALID_STATUS_CLASSES = { textClass: STATUS_TEXT_RED, rowClass: ROW_COLOR_RED };
